import { memo, MouseEvent, ReactElement, ReactNode, useCallback } from 'react'

import { Icon, IconName } from '../Icon'
import * as Styled from './styles'
import { TagColor, TagSize, TagType } from './types'
import { tagIconSize } from './utils/getSizeStyle'

export interface TagProps {
  children: ReactNode
  color?: TagColor
  disabled?: boolean
  icon?: IconName
  maxWidth?: string
  onClick?: (event: MouseEvent<HTMLSpanElement>) => void
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void
  selected?: boolean
  size?: TagSize
  type?: TagType
}

export const Tag = memo(
  ({
    children,
    color = 'neutral',
    disabled = false,
    icon,
    maxWidth,
    onClick,
    onClose,
    selected = false,
    size = 'md',
    type = 'primary',
  }: TagProps): ReactElement => {
    const clickable = !!onClick && !disabled

    const handleClick = useCallback(
      (event: MouseEvent<HTMLSpanElement>) => {
        if (!clickable) {
          return
        }
        onClick?.(event)
      },
      [clickable, onClick],
    )

    const handleCloseClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onClose?.(event)
      },
      [onClose],
    )

    return (
      <Styled.TagWrapper
        aria-disabled={disabled || undefined}
        aria-pressed={clickable ? selected : undefined}
        role={clickable ? 'button' : undefined}
        $clickable={clickable}
        data-testid="tag-wrapper"
        $disabled={disabled}
        $maxWidth={maxWidth}
        onClick={handleClick}
        $selected={clickable ? selected : false}
        $tagColor={color}
        $tagSize={size}
        $tagType={type}
      >
        {icon && <Icon icon={icon} size={tagIconSize[size]} color="currentColor" />}

        {children}

        {!!onClose && (
          <Styled.CloseButton aria-label="Remove tag" disabled={disabled} onClick={handleCloseClick} type="button">
            <Icon icon="x" size={tagIconSize[size]} color="currentColor" />
          </Styled.CloseButton>
        )}
      </Styled.TagWrapper>
    )
  },
)
